import * as v from 'valibot'

export const NOVEL_PLAY_LANGUAGES = Object.freeze(['en', 'zh', 'ja', 'ko', 'es', 'pt'])
export const NovelPlayLanguage = v.picklist(NOVEL_PLAY_LANGUAGES)
const Text = v.pipe(v.string(), v.maxLength(100000))
const VoiceId = v.nullable(v.pipe(v.string(), v.minLength(1), v.maxLength(80)))
const Fields = v.record(v.string(), Text)

/** Public prose only. Entity IDs, source evidence, private profiles and asset URLs are never translated. */
export const NovelReaderLocalization = v.strictObject({
  source_hash: v.string(),
  title: Text,
  synopsis: v.optional(v.pipe(v.string(), v.maxLength(1200)), ''),
  opening_text: Text,
  opening_segments: v.optional(v.array(v.strictObject({ speaker_id: v.string(), channel: v.string(), text: Text })), () => []),
  // Read-only compatibility: preserve frozen configuration hashes; never generate or translate this retired field.
  continuation_text: v.optional(Text),
  world_rules: Text,
  characters: v.record(v.string(), v.strictObject({ name: Text, public_profile: Text, appearance: Text })),
  locations: v.record(v.string(), v.strictObject({ name: Text, description: Text })),
  items: v.record(v.string(), v.strictObject({ name: Text, description: Text })),
  facts: Fields,
  relations: Fields,
  initial_threads: Fields,
})
export const NovelVoiceProfile = v.strictObject({
  narrator_voice_id: VoiceId,
  player_voice_id: VoiceId,
  character_voices: v.record(v.string(), VoiceId),
})
export const NovelPlayConfig = v.strictObject({
  schema_version: v.literal(1),
  default_play_language: v.literal('en'),
  play_languages: v.array(NovelPlayLanguage),
  non_speaking_character_ids: v.optional(v.array(v.pipe(v.string(), v.minLength(1)))),
  voice_profiles: v.record(NovelPlayLanguage, NovelVoiceProfile),
  reader_localizations: v.record(NovelPlayLanguage, NovelReaderLocalization),
})
export const NovelStudioBundle = v.strictObject({
  schema_version: v.literal(1),
  content: v.record(v.string(), v.unknown()),
  play_config: v.nullable(NovelPlayConfig),
})

export function emptyNovelPlayConfig() {
  return { schema_version: 1, default_play_language: 'en', play_languages: [], non_speaking_character_ids: [], voice_profiles: {}, reader_localizations: {} }
}

/** Unset language selections inherit English; explicit English NoVoice stays silent in every language. */
export function resolveNovelVoiceProfile(config, language) {
  const english = config?.voice_profiles?.en
  const selected = config?.voice_profiles?.[language]
  const non_speaking = new Set(config?.non_speaking_character_ids ?? [])
  const ids = new Set([...Object.keys(english?.character_voices ?? {}), ...Object.keys(selected?.character_voices ?? {}), ...non_speaking])
  const voiceFor = (profile, id) => Object.hasOwn(profile?.character_voices ?? {}, id) ? profile.character_voices[id] : null
  return {
    narrator_voice_id: english?.narrator_voice_id === null ? null : selected?.narrator_voice_id ?? english?.narrator_voice_id ?? null,
    player_voice_id: selected?.player_voice_id ?? english?.player_voice_id ?? null,
    character_voices: Object.fromEntries([...ids].map(id => [id, non_speaking.has(id) || english?.character_voices?.[id] === null
      ? null : voiceFor(selected, id) ?? voiceFor(english, id) ?? null])),
  }
}

/** Captures the editable reader fields in the source language without calling a translation model. */
export function novelReaderLocalization(content, source_hash) {
  const text = value => typeof value === 'string' ? value : ''
  const byId = (items, fields) => Object.fromEntries((items || []).map(item => [item.id, Object.fromEntries(fields.map(field => [field, text(item[field])]))]))
  const publicFacts = (content.facts || []).filter(item => item.observer_ids?.includes(content.player_id))
  const publicRelations = (content.relations || []).map((item, index) => ({ item, index })).filter(({ item }) => item.observer_ids?.includes(content.player_id))
  return {
    source_hash,
    title: text(content.title),
    synopsis: text(content.synopsis),
    opening_text: text(content.opening?.text),
    opening_segments: (content.opening?.segments || []).map(item => ({ speaker_id: item.speaker_id, channel: item.channel, text: !Array.isArray(item.audience_ids) || item.audience_ids.includes(content.player_id) ? text(item.text) : '' })),
    world_rules: text(content.world_rules),
    characters: Object.fromEntries((content.characters || []).map(item => [item.id, { name: text(item.name), public_profile: text(item.public_profile), appearance: text(item.profile?.appearance) }])),
    locations: byId(content.locations, ['name', 'description']),
    items: byId(content.items, ['name', 'description']),
    facts: Object.fromEntries(publicFacts.map(item => [item.id, text(item.text)])),
    relations: Object.fromEntries(publicRelations.map(({ item, index }) => [String(index), text(item.text)])),
    initial_threads: Object.fromEntries((content.initial_threads || []).filter(item => item.participant_ids?.includes(content.player_id)).map(item => [item.id, text(item.text)])),
  }
}

/** Missing/stale localization fails before opening; no implicit translation or source-language fallback. */
export function novelLocalizationIssues(content, config, source_hash, language) {
  const locale = config?.reader_localizations?.[language]
  const errors = []
  const missing = path => errors.push({ code: 'reader_localization_missing', path })
  if (!locale) return [{ code: 'reader_localization_missing', path: `reader_localizations.${language}` }]
  if (locale.source_hash !== source_hash) errors.push({ code: 'reader_localization_stale', path: `reader_localizations.${language}.source_hash` })
  const reference = novelReaderLocalization(content, source_hash)
  for (const field of ['title', 'synopsis', 'opening_text', 'world_rules']) if (reference[field] && !locale[field]?.trim()) missing(field)
  const segments = locale.opening_segments || []
  if (segments.length !== reference.opening_segments.length) errors.push({ code: 'reader_opening_structure_changed', path: 'opening_segments' })
  for (const [index, source] of reference.opening_segments.entries()) {
    const selected = segments[index]
    if (selected?.speaker_id !== source.speaker_id || selected?.channel !== source.channel) errors.push({ code: 'reader_opening_structure_changed', path: `opening_segments.${index}` })
    if (source.text && !selected?.text?.trim()) missing(`opening_segments.${index}.text`)
    const original = content.opening.segments[index]
    if (Array.isArray(original.audience_ids) && !original.audience_ids.includes(content.player_id) && selected?.text) errors.push({ code: 'reader_opening_private_segment', path: `opening_segments.${index}.text` })
  }
  for (const group of ['characters', 'locations', 'items']) {
    for (const [id, fields] of Object.entries(reference[group])) {
      for (const [field, source] of Object.entries(fields)) if (source && !locale[group]?.[id]?.[field]?.trim()) missing(`${group}.${id}.${field}`)
    }
    for (const id of Object.keys(locale[group] || {})) if (!Object.hasOwn(reference[group], id)) errors.push({ code: 'reader_localization_unknown_id', path: `${group}.${id}` })
  }
  for (const group of ['facts', 'relations', 'initial_threads']) {
    for (const [id, source] of Object.entries(reference[group])) if (source && !locale[group]?.[id]?.trim()) missing(`${group}.${id}`)
    for (const id of Object.keys(locale[group] || {})) if (!Object.hasOwn(reference[group], id)) errors.push({ code: 'reader_localization_unknown_id', path: `${group}.${id}` })
  }
  return errors
}

/** Applies reader prose to a copy, leaving language/source hash and all private rules intact. */
export function projectNovelReaderContent(content, config, language) {
  const locale = config?.reader_localizations?.[language]
  if (!locale) throw new Error('reader_localization_missing')
  const result = structuredClone(content)
  result.title = locale.title
  result.synopsis = locale.synopsis ?? ''
  result.world_rules = locale.world_rules
  delete result.continuation
  if (Array.isArray(content.opening?.segments) && content.opening.segments.length > 0) {
    const selected = locale.opening_segments || []
    if (selected.length !== content.opening.segments.length || selected.some((item, index) => item.speaker_id !== content.opening.segments[index].speaker_id || item.channel !== content.opening.segments[index].channel || ((!Array.isArray(content.opening.segments[index].audience_ids) || content.opening.segments[index].audience_ids.includes(content.player_id)) ? !item.text?.trim() : Boolean(item.text)))) throw new Error('reader_opening_structure_changed')
    result.opening.segments = result.opening.segments.map((item, index) => ({ ...item, text: !Array.isArray(item.audience_ids) || item.audience_ids.includes(content.player_id) ? selected[index].text : item.text }))
    delete result.opening.text
  } else {
    if (locale.opening_segments?.length) throw new Error('reader_opening_structure_changed')
    result.opening = { ...result.opening, text: locale.opening_text }
    delete result.opening.segments
  }
  result.characters = (result.characters || []).map(item => ({ ...item, name: locale.characters[item.id]?.name ?? item.name, public_profile: locale.characters[item.id]?.public_profile ?? item.public_profile, profile: { ...item.profile, appearance: locale.characters[item.id]?.appearance ?? item.profile?.appearance } }))
  for (const group of ['locations', 'items']) result[group] = (result[group] || []).map(item => ({ ...item, ...locale[group][item.id] }))
  for (const group of ['facts', 'initial_threads']) result[group] = (result[group] || []).map(item => ({ ...item, text: locale[group][item.id] ?? item.text }))
  result.relations = (result.relations || []).map((item, index) => ({ ...item, text: item.observer_ids?.includes(content.player_id) ? locale.relations[String(index)] ?? item.text : item.text }))
  return result
}

export function novelVoiceSupportsLanguage(voice, language) {
  // English is the required base: a multilingual capability cannot relabel a non-English or unknown voice.
  if (language === 'en') return typeof voice.language_code === 'string' && voice.language_code.trim().toLowerCase().split(/[-_]/)[0] === 'en'
  const supported = voice.supported_languages?.length ? voice.supported_languages : [voice.language_code]
  return supported.some(value => typeof value === 'string' && value.toLowerCase().split(/[-_]/)[0] === language)
}

/** Validate character selections in their declared languages; the reader does not speak narration. */
export function novelVoiceConfigIssues(config, voices) {
  const errors = []
  const catalog = new Map(voices.map(voice => [voice.voice_id, voice]))
  for (const [language, profile] of Object.entries(config?.voice_profiles || {})) {
    const selected = [profile.player_voice_id, ...Object.values(profile.character_voices)]
    for (const voice_id of selected.filter(Boolean)) {
      const voice = catalog.get(voice_id)
      if (!voice || !novelVoiceSupportsLanguage(voice, language)) errors.push({ code: 'voice_language_unavailable', path: `voice_profiles.${language}`, voice_id })
    }
  }
  return errors
}

export function novelPublicationIssues(content, config, source_hash, voices = null) {
  const errors = []
  if (!config?.play_languages?.includes('en')) errors.push({ code: 'english_play_language_required', path: 'play_languages' })
  const languages = config?.play_languages || []
  if (new Set(languages).size !== languages.length) errors.push({ code: 'duplicate_play_language', path: 'play_languages' })
  for (const language of languages) errors.push(...novelLocalizationIssues(content, config, source_hash, language))
  const non_speaking = new Set(config?.non_speaking_character_ids || [])
  const npcs = (content.characters || []).filter(item => item.id !== content.player_id)
  if (non_speaking.size !== (config?.non_speaking_character_ids || []).length) errors.push({ code: 'duplicate_non_speaking_character', path: 'non_speaking_character_ids' })
  for (const id of non_speaking) if (!npcs.some(item => item.id === id)) errors.push({ code: 'non_speaking_character_invalid', path: 'non_speaking_character_ids', character_id: id })
  for (const [language, profile] of Object.entries(config?.voice_profiles || {})) {
    for (const id of non_speaking) if (profile.character_voices?.[id]) errors.push({ code: 'non_speaking_character_voice', path: `voice_profiles.${language}.character_voices.${id}` })
  }
  const english = config?.voice_profiles?.en
  const required = npcs.filter(item => !non_speaking.has(item.id)).map(item => [`character_voices.${item.id}`, english?.character_voices?.[item.id]])
  // null is an explicit NoVoice selection; a missing English key still requires configuration.
  for (const [path, voice_id] of required) if (voice_id !== null && !voice_id) errors.push({ code: 'english_voice_required', path: `voice_profiles.en.${path}` })
  // Opening checks structural readiness without a catalogue call; activation also verifies real resources.
  if (voices === null) return errors
  errors.push(...novelVoiceConfigIssues(config, voices))
  return errors
}
